'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Folder,
  File,
  Plus,
  Upload,
  MoreHorizontal,
  Trash2,
  Pencil,
  FolderPlus,
  ChevronRight,
  ArrowLeft,
  Copy,
  Loader2,
  FileUp,
  X,
  Grid3X3,
  List,
  Cloud,
  User,
  Shield,
  ShieldAlert,
} from 'lucide-react'
import { AppLogo } from '@/components/app-logo'
import SmartLink from '@/components/smart-link'
import { AuthButton } from '@/components/auth/auth-button'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabaseClient'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useSynopticProject, SynopticFolder } from '@/lib/synoptic-project-provider'
import { useAuth } from '@/lib/auth-provider'
import { useLanguage } from '@/lib/i18n/language-provider'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'

interface ProjectHubProps {
  onProjectOpen: (projectId: string) => void
}

interface SynopticProject {
  id: string
  user_id: string
  name: string
  description?: string
  data: any
  thumbnail_url?: string
  last_opened_at: string
  created_at: string
  updated_at: string
  version: number
  folder_id?: string | null
  read_only?: boolean
  is_admin_view?: boolean
  profiles?: {
    email: string
    display_name: string
  }
}

function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
}

function formatRelativeTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  const now = new Date()
  const diff = now.getTime() - d.getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return "now"
  if (minutes < 60) return `${minutes}m`
  if (hours < 24) return `${hours}h`
  if (days < 7) return `${days}d`
  return formatDate(d)
}

function ProjectSkeleton() {
  return (
    <div className="p-4 rounded-lg bg-card border border-border">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded" />
          <Skeleton className="h-5 w-32" />
        </div>
      </div>
      <div className="space-y-2">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-3 w-20" />
      </div>
    </div>
  )
}

function FolderSkeleton() {
  return (
    <div className="flex items-center justify-between p-4 rounded-lg bg-card border border-border">
      <div className="flex items-center gap-3">
        <Skeleton className="h-5 w-5 rounded" />
        <Skeleton className="h-5 w-28" />
        <Skeleton className="h-4 w-4" />
      </div>
    </div>
  )
}


export function ProjectHub({ onProjectOpen }: ProjectHubProps) {
  const { user, isPro } = useAuth()
  const { t } = useLanguage()
  
  // Mode admin pour voir tous les projets
  const [isAdminMode, setIsAdminMode] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  
  const {
    projects,
    loadProjects,
    createProject,
    deleteProject,
    duplicateProject,
    updateProjectName,
    folders,
    loadFolders,
    createFolder,
    updateFolder,
    deleteFolder,
    moveProjectToFolder,
  } = useSynopticProject()

  const [currentFolder, setCurrentFolder] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')

  const [dialogType, setDialogType] = useState<'project' | 'folder' | 'rename-project' | 'rename-folder' | 'import' | null>(null)
  const [inputValue, setInputValue] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [editingItem, setEditingItem] = useState<{ type: 'project' | 'folder'; id: string; name: string } | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'project' | 'folder'; id: string; name: string } | null>(null)
  const [importFiles, setImportFiles] = useState<File[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [openingProjectId, setOpeningProjectId] = useState<string | null>(null)
  const [openingProjectName, setOpeningProjectName] = useState<string | null>(null)
  const [isImportingFile, setIsImportingFile] = useState(false)
  const [importingFileName, setImportingFileName] = useState<string | null>(null)
  
  // Vérifier si l'utilisateur est admin
  useEffect(() => {
    const checkAdminRole = async () => {
      if (!user) return
      
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single()
        
        setIsAdmin(profile?.role === 'admin')
      } catch (error) {
        setIsAdmin(false)
      }
    }
    
    checkAdminRole()
  }, [user])

  const currentFolderData = folders.find((f) => f.id === currentFolder)

  const visibleProjects = projects.filter((p) => {
    return (p.folder_id || null) === currentFolder
  })

  const visibleFolders = currentFolder === null ? folders : []
  
  // Grouper les projets par utilisateur en mode admin
  const projectsByUser = isAdminMode 
    ? projects.reduce((acc: Record<string, SynopticProject[]>, project: SynopticProject) => {
        const userId = project.user_id
        if (!acc[userId]) {
          acc[userId] = []
        }
        acc[userId].push(project)
        return acc
      }, {} as Record<string, SynopticProject[]>)
    : {} as Record<string, SynopticProject[]>

  // Refs to store latest function references without triggering re-renders
  const loadProjectsRef = useRef(loadProjects)
  const loadFoldersRef = useRef(loadFolders)
  
  // Keep refs updated
  useEffect(() => {
    loadProjectsRef.current = loadProjects
    loadFoldersRef.current = loadFolders
  }, [loadProjects, loadFolders])
  
  // Track if we've already loaded to prevent duplicate loads
  const hasLoadedRef = useRef(false)

  useEffect(() => {
    const loadData = async () => {
      if (hasLoadedRef.current) return
      hasLoadedRef.current = true
      setIsLoading(true)
      try {
        await Promise.all([loadProjectsRef.current(isAdminMode), loadFoldersRef.current()])
      } finally {
        setIsLoading(false)
      }
    }

    if (user && (isPro || isAdminMode)) {
      loadData()
    } else {
      setIsLoading(false)
    }
    
    // Reset on user change or admin mode change
    return () => {
      hasLoadedRef.current = false
    }
  }, [user?.id, isPro, isAdminMode])

  const handleCreateProject = async () => {
    if (!inputValue.trim() || isCreating) return
    setIsCreating(true)
    try {
      const newProject = await createProject(inputValue.trim())
      if (currentFolder) {
        await moveProjectToFolder(newProject.id, currentFolder)
      }
      setInputValue('')
      setDialogType(null)
      onProjectOpen(newProject.id)
    } catch (err) {
      // console.error('Failed to create project:', err)
    } finally {
      setIsCreating(false)
    }
  }

  const handleCreateFolder = async () => {
    if (!inputValue.trim()) return
    try {
      await createFolder(inputValue.trim())
      setInputValue('')
      setDialogType(null)
    } catch (err) {
      // console.error('Failed to create folder:', err)
    }
  }

  const handleRenameProject = async () => {
    if (!inputValue.trim() || !editingItem) return
    // Passer le projectId pour renommer le bon projet
    await updateProjectName(inputValue.trim(), editingItem.id)
    setInputValue('')
    setDialogType(null)
    setEditingItem(null)
  }

  const handleRenameFolder = async () => {
    if (!inputValue.trim() || !editingItem) return
    await updateFolder(editingItem.id, inputValue.trim())
    setInputValue('')
    setDialogType(null)
    setEditingItem(null)
  }

  const handleDeleteProject = async () => {
    if (!deleteConfirm) return
    await deleteProject(deleteConfirm.id)
    setDeleteConfirm(null)
  }

  const handleDeleteFolder = async () => {
    if (!deleteConfirm) return
    await deleteFolder(deleteConfirm.id)
    setDeleteConfirm(null)
  }

  const handleMoveToFolder = async (projectId: string, folderId: string | null) => {
    await moveProjectToFolder(projectId, folderId)
  }

  const handleDuplicateProject = async (projectId: string) => {
    try {
      const originalProject = projects.find(p => p.id === projectId)
      const duplicated = await duplicateProject(projectId)
      if (originalProject?.folder_id) {
        await moveProjectToFolder(duplicated.id, originalProject.folder_id)
      }
    } catch (err) {
      // console.error('Failed to duplicate project:', err)
    }
  }

  const handleImport = async () => {
    if (importFiles.length === 0) return
    
    // Show loading animation
    const firstFileName = importFiles[0]?.name.replace('.vgsyn', '') || 'Projet'
    setImportingFileName(firstFileName)
    setIsImportingFile(true)
    setDialogType(null)
    
    // Small delay to show the animation
    await new Promise(resolve => setTimeout(resolve, 800))
    
    setIsCreating(true)
    let successCount = 0
    let errorCount = 0
    
    for (const file of importFiles) {
      try {
        const content = await file.text()
        const data = JSON.parse(content)
        
        // Validate that this is a synoptic file
        if (data.type !== 'synoptic') {
          toast.error(t('projectHub.invalidFile'))
          errorCount++
          continue
        }
        
        // Create a new project with the imported data
        const projectName = data.projectName || file.name.replace('.vgsyn', '')
        const newProject = await createProject(projectName, data)
        
        // Move to current folder if we're in one
        if (currentFolder && newProject) {
          await moveProjectToFolder(newProject.id, currentFolder)
        }
        
        successCount++
      } catch (err) {
        errorCount++
        toast.error(t('projectHub.importError'))
      }
    }
    
    if (successCount > 0) {
      toast.success(t('projectHub.importSuccess'))
    }
    
    setIsCreating(false)
    setImportFiles([])
    setIsImportingFile(false)
    setImportingFileName(null)
  }

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const droppedFiles = Array.from(e.dataTransfer.files).filter((f) =>
      f.name.endsWith('.vgsyn')
    )
    setImportFiles((prev) => [...prev, ...droppedFiles])
  }, [])

  const handleOpenProject = async (projectId: string) => {
    const project = projects.find(p => p.id === projectId)
    setOpeningProjectId(projectId)
    setOpeningProjectName(project?.name || 'Projet')
    
    // Small delay to show the animation before navigation
    await new Promise(resolve => setTimeout(resolve, 800))
    
    onProjectOpen(projectId)
  }

  const openRenameDialog = (type: 'project' | 'folder', id: string, name: string) => {
    setEditingItem({ type, id, name })
    setInputValue(name)
    setDialogType(type === 'project' ? 'rename-project' : 'rename-folder')
  }

  if (!user || !isPro) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">{t('projectHub.proRequired')}</h2>
          <p className="text-muted-foreground">{t('projectHub.proRequiredDesc')}</p>
        </div>
      </div>
    )
  }

  // Loading overlay for project opening from cloud
  if (openingProjectId) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
        <div className="flex flex-col items-center gap-6 animate-in fade-in zoom-in duration-300">
          {/* Cloud animation */}
          <div className="relative">
            <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl animate-pulse" />
            <div className="relative bg-gradient-to-br from-primary/10 to-primary/5 p-8 rounded-full border border-primary/20">
              <Cloud className="h-16 w-16 text-primary animate-bounce" />
            </div>
          </div>
          
          {/* Loading text */}
          <div className="text-center space-y-2">
            <h2 className="text-xl font-semibold text-foreground">
              {t('projectHub.openingFromCloud') || 'Ouverture depuis VisualCloud...'}
            </h2>
            <p className="text-muted-foreground">
              {openingProjectName}
            </p>
          </div>
          
          {/* Progress bar */}
          <div className="w-64 h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full animate-progress" 
                 style={{ 
                   animation: 'progress 0.8s ease-out forwards',
                 }} 
            />
          </div>
        </div>
        
        {/* CSS for progress animation */}
        <style jsx>{`
          @keyframes progress {
            from { width: 0%; }
            to { width: 100%; }
          }
          .animate-progress {
            animation: progress 0.8s ease-out forwards;
          }
        `}</style>
      </div>
    )
  }

  // Loading overlay for file import
  if (isImportingFile) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
        <div className="flex flex-col items-center gap-6 animate-in fade-in zoom-in duration-300">
          {/* File animation */}
          <div className="relative">
            <div className="absolute inset-0 bg-purple-500/20 rounded-full blur-xl animate-pulse" />
            <div className="relative bg-gradient-to-br from-purple-500/10 to-purple-500/5 p-8 rounded-full border border-purple-500/20">
              <FileUp className="h-16 w-16 text-purple-500 animate-bounce" />
            </div>
          </div>
          
          {/* Loading text */}
          <div className="text-center space-y-2">
            <h2 className="text-xl font-semibold text-foreground">
              {t('projectHub.importingFile') || 'Import du projet...'}
            </h2>
            <p className="text-muted-foreground">
              {importingFileName}
            </p>
          </div>
          
          {/* Progress bar */}
          <div className="w-64 h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-purple-500 rounded-full" 
                 style={{ 
                   animation: 'progress 0.8s ease-out forwards',
                 }} 
            />
          </div>
        </div>
        
        {/* CSS for progress animation */}
        <style jsx>{`
          @keyframes progress {
            from { width: 0%; }
            to { width: 100%; }
          }
        `}</style>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top bar with logo and profile */}
      <header className="border-b border-border px-6 py-3 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex items-center justify-between">
          {/* Left: Logo and back navigation */}
          <div className="flex items-center gap-4">
            <SmartLink href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <AppLogo className="w-8 h-8" />
              <span className="font-semibold text-lg hidden sm:inline">VisualGrid</span>
            </SmartLink>
          </div>
          
          {/* Right: Profile */}
          <div className="flex items-center gap-3">
            <AuthButton />
          </div>
        </div>
      </header>

      {/* Secondary header with project controls */}
      <header className="border-b border-border px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold tracking-tight">{t('projectHub.title')}</h1>
            {currentFolder && (
              <>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">{currentFolderData?.name}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Admin mode toggle */}
            {isAdmin && (
              <Button
                variant={isAdminMode ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setIsAdminMode(!isAdminMode)
                  hasLoadedRef.current = false // Force reload
                }}
                className={cn(
                  "gap-2",
                  isAdminMode && "bg-purple-600 hover:bg-purple-700"
                )}
              >
                {isAdminMode ? <ShieldAlert className="h-4 w-4" /> : <Shield className="h-4 w-4" />}
                {isAdminMode ? 'Mode Admin' : 'Voir tous'}
              </Button>
            )}
            
            <div className="flex items-center border border-border rounded-md p-0.5">
              <Button
                variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                size="icon"
                className="h-7 w-7"
                onClick={() => setViewMode('grid')}
              >
                <Grid3X3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="icon"
                className="h-7 w-7"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setImportFiles([]); setDialogType('import') }}
              className="text-muted-foreground hover:text-foreground"
            >
              <Upload className="h-4 w-4 mr-2" />
              {t('projectHub.import')}
            </Button>
            {currentFolder === null && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setInputValue(''); setDialogType('folder') }}
                className="text-muted-foreground hover:text-foreground"
              >
                <FolderPlus className="h-4 w-4 mr-2" />
                {t('projectHub.newFolder')}
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => { setInputValue(''); setDialogType('project') }}
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('projectHub.newProject')}
            </Button>
          </div>
        </div>
      </header>

      <main className="p-6">
        {currentFolder && (
          <button
            onClick={() => setCurrentFolder(null)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('projectHub.back')}
          </button>
        )}

        {(visibleFolders.length > 0 || (isLoading && currentFolder === null)) && (
          <div className="mb-8">
            <h2 className="text-sm font-medium text-muted-foreground mb-3">{t('projectHub.folders')}</h2>
            <div className={cn(
              'grid gap-3',
              viewMode === 'grid'
                ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                : 'grid-cols-1'
            )}>
              {isLoading ? (
                [...Array(2)].map((_, i) => <FolderSkeleton key={i} />)
              ) : (visibleFolders.map((folder) => {
                const count = projects.filter((p) => p.folder_id === folder.id).length
                return (
                  <div
                    key={folder.id}
                    className="group flex items-center justify-between p-4 rounded-lg bg-card border border-border hover:border-muted-foreground/50 cursor-pointer transition-colors"
                    onClick={() => setCurrentFolder(folder.id)}
                  >
                    <div className="flex items-center gap-3">
                      <Folder className="h-5 w-5 text-muted-foreground" />
                      <span className="font-medium">{folder.name}</span>
                      <span className="text-sm text-muted-foreground">{count}</span>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation()
                            openRenameDialog('folder', folder.id, folder.name)
                          }}
                        >
                          <Pencil className="h-4 w-4 mr-2" />
                          {t('projectHub.rename')}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation()
                            setDeleteConfirm({ type: 'folder', id: folder.id, name: folder.name })
                          }}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          {t('projectHub.delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )
              }))}
            </div>
          </div>
        )}

        <div>
          <h2 className="text-sm font-medium text-muted-foreground mb-3">
            {t('projectHub.projects')}
            {isAdminMode && (
              <span className="ml-2 text-purple-600 font-semibold">Mode Admin</span>
            )}
          </h2>
          {isLoading ? (
            <div className={cn(
              'grid gap-3',
              viewMode === 'grid'
                ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                : 'grid-cols-1'
            )}>
              {[...Array(4)].map((_, i) => (
                <ProjectSkeleton key={i} />
              ))}
            </div>
          ) : isAdminMode ? (
            // Mode admin : grouper par utilisateur
            Object.keys(projectsByUser).length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <File className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Aucun projet trouvé</p>
              </div>
            ) : (
              <div className="space-y-8">
                {Object.entries(projectsByUser).map(([userId, userProjects]) => {
                  const userProfile = userProjects[0]?.profiles
                  const userName = userProfile?.display_name || userProfile?.email || 'Utilisateur inconnu'
                  
                  return (
                    <div key={userId}>
                      <div className="flex items-center gap-2 mb-4 pb-2 border-b border-border">
                        <User className="h-5 w-5 text-purple-600" />
                        <h3 className="text-lg font-semibold">{userName}</h3>
                        <span className="text-sm text-muted-foreground">
                          ({userProjects.length} projet{userProjects.length > 1 ? 's' : ''})
                        </span>
                      </div>
                      <div className={cn(
                        'grid gap-3',
                        viewMode === 'grid'
                          ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                          : 'grid-cols-1'
                      )}>
                        {userProjects.map((project) => (
                          <div
                            key={project.id}
                            className={cn(
                              'group rounded-lg bg-card border border-purple-500/30 hover:border-purple-500 cursor-pointer transition-colors',
                              viewMode === 'grid' ? 'p-4' : 'p-3 flex items-center gap-4'
                            )}
                            onClick={() => handleOpenProject(project.id)}
                          >
                            {viewMode === 'grid' ? (
                              <>
                                <div className="flex items-start justify-between mb-3">
                                  <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 rounded bg-purple-100 dark:bg-purple-900/20 flex items-center justify-center">
                                      <File className="h-5 w-5 text-purple-600" />
                                    </div>
                                    <span className="font-medium truncate max-w-[150px]">{project.name}</span>
                                  </div>
                                </div>
                                <div className="text-xs text-muted-foreground space-y-1">
                                  <p>{t('projectHub.createdAt')} {formatDate(project.created_at)}</p>
                                  <p>{t('projectHub.lastOpened')} {formatRelativeTime(project.updated_at)}</p>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="h-10 w-10 rounded bg-purple-100 dark:bg-purple-900/20 flex items-center justify-center shrink-0">
                                  <File className="h-5 w-5 text-purple-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <span className="font-medium truncate block">{project.name}</span>
                                  <span className="text-xs text-muted-foreground">{formatRelativeTime(project.updated_at)}</span>
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            )
          ) : visibleProjects.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <File className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('projectHub.noProjects')}</p>
              <p className="text-sm mt-1">{t('projectHub.noProjectsDesc')}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => { setInputValue(''); setDialogType('project') }}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('projectHub.newProject')}
              </Button>
            </div>
          ) : (
            <div className={cn(
              'grid gap-3',
              viewMode === 'grid'
                ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                : 'grid-cols-1'
            )}>
              {visibleProjects.map((project) => (
                <div
                  key={project.id}
                  className={cn(
                    'group rounded-lg bg-card border border-border hover:border-muted-foreground/50 cursor-pointer transition-colors',
                    viewMode === 'grid' ? 'p-4' : 'p-3 flex items-center gap-4'
                  )}
                  onClick={() => handleOpenProject(project.id)}
                >
                  {viewMode === 'grid' ? (
                    <>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded bg-muted flex items-center justify-center">
                            <File className="h-5 w-5 text-muted-foreground" />
                          </div>
                          <span className="font-medium truncate max-w-[150px]">{project.name}</span>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openRenameDialog('project', project.id, project.name) }}>
                              <Pencil className="h-4 w-4 mr-2" />
                              {t('projectHub.rename')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDuplicateProject(project.id) }}>
                              <Copy className="h-4 w-4 mr-2" />
                              {t('projectHub.duplicate')}
                            </DropdownMenuItem>
                            <DropdownMenuSub>
                              <DropdownMenuSubTrigger onClick={(e) => e.stopPropagation()}>
                                <Folder className="h-4 w-4 mr-2" />
                                {t('projectHub.move')}
                              </DropdownMenuSubTrigger>
                              <DropdownMenuSubContent>
                                {project.folder_id && (
                                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleMoveToFolder(project.id, null) }}>
                                    {t('projectHub.root')}
                                  </DropdownMenuItem>
                                )}
                                {folders.filter((f) => f.id !== project.folder_id).map((folder) => (
                                  <DropdownMenuItem
                                    key={folder.id}
                                    onClick={(e) => { e.stopPropagation(); handleMoveToFolder(project.id, folder.id) }}
                                  >
                                    {folder.name}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuSubContent>
                            </DropdownMenuSub>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={(e) => { e.stopPropagation(); setDeleteConfirm({ type: 'project', id: project.id, name: project.name }) }}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              {t('projectHub.delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <div className="text-xs text-muted-foreground space-y-1">
                        <p>{t('projectHub.createdAt')} {formatDate(project.created_at)}</p>
                        <p>{t('projectHub.lastOpened')} {formatRelativeTime(project.updated_at)}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="h-10 w-10 rounded bg-muted flex items-center justify-center shrink-0">
                        <File className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="font-medium truncate block">{project.name}</span>
                        {isAdminMode && project.profiles && (
                          <span className="text-xs text-purple-600 flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {project.profiles.display_name || project.profiles.email}
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground">{formatRelativeTime(project.updated_at)}</span>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openRenameDialog('project', project.id, project.name) }}>
                            <Pencil className="h-4 w-4 mr-2" />
                            {t('projectHub.rename')}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDuplicateProject(project.id) }}>
                            <Copy className="h-4 w-4 mr-2" />
                            {t('projectHub.duplicate')}
                          </DropdownMenuItem>
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger onClick={(e) => e.stopPropagation()}>
                              <Folder className="h-4 w-4 mr-2" />
                              {t('projectHub.move')}
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent>
                              {project.folder_id && (
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleMoveToFolder(project.id, null) }}>
                                  {t('projectHub.root')}
                                </DropdownMenuItem>
                              )}
                              {folders.filter((f) => f.id !== project.folder_id).map((folder) => (
                                <DropdownMenuItem
                                  key={folder.id}
                                  onClick={(e) => { e.stopPropagation(); handleMoveToFolder(project.id, folder.id) }}
                                >
                                  {folder.name}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={(e) => { e.stopPropagation(); setDeleteConfirm({ type: 'project', id: project.id, name: project.name }) }}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            {t('projectHub.delete')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Dialog open={dialogType === 'project'} onOpenChange={(open) => !open && setDialogType(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('projectHub.dialogNewProject')}</DialogTitle>
            <DialogDescription>{t('projectHub.dialogNewProjectDesc')}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="project-name" className="text-xs text-muted-foreground">{t('projectHub.projectName')}</Label>
            <Input
              id="project-name"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateProject()}
              placeholder={t('projectHub.projectName')}
              className="mt-1.5"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogType(null)}>{t('projectHub.cancel')}</Button>
            <Button onClick={handleCreateProject} disabled={!inputValue.trim() || isCreating}>{isCreating ? '...' : t('projectHub.create')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogType === 'folder'} onOpenChange={(open) => !open && setDialogType(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('projectHub.dialogNewFolder')}</DialogTitle>
            <DialogDescription>{t('projectHub.dialogNewFolderDesc')}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="folder-name" className="text-xs text-muted-foreground">{t('projectHub.folderName')}</Label>
            <Input
              id="folder-name"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
              placeholder={t('projectHub.folderName')}
              className="mt-1.5"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogType(null)}>{t('projectHub.cancel')}</Button>
            <Button onClick={handleCreateFolder} disabled={!inputValue.trim()}>{t('projectHub.create')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogType === 'rename-project'} onOpenChange={(open) => { if (!open) { setDialogType(null); setEditingItem(null) } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('projectHub.dialogRenameProject')}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="rename-project" className="text-xs text-muted-foreground">{t('projectHub.projectName')}</Label>
            <Input
              id="rename-project"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleRenameProject()}
              className="mt-1.5"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setDialogType(null); setEditingItem(null) }}>{t('projectHub.cancel')}</Button>
            <Button onClick={handleRenameProject} disabled={!inputValue.trim() || inputValue === editingItem?.name}>{t('projectHub.rename')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogType === 'rename-folder'} onOpenChange={(open) => { if (!open) { setDialogType(null); setEditingItem(null) } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('projectHub.dialogRenameFolder')}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="rename-folder" className="text-xs text-muted-foreground">{t('projectHub.folderName')}</Label>
            <Input
              id="rename-folder"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleRenameFolder()}
              className="mt-1.5"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setDialogType(null); setEditingItem(null) }}>{t('projectHub.cancel')}</Button>
            <Button onClick={handleRenameFolder} disabled={!inputValue.trim() || inputValue === editingItem?.name}>{t('projectHub.rename')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogType === 'import'} onOpenChange={(open) => { if (!open) { setDialogType(null); setImportFiles([]) } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('projectHub.dialogImport')}</DialogTitle>
            <DialogDescription>{t('projectHub.dialogImportDesc')}</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div
              className={cn(
                'relative border-2 border-dashed rounded-lg p-8 text-center transition-all',
                isDragging ? 'border-primary bg-primary/10' : 'border-border hover:border-muted-foreground/50'
              )}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleFileDrop}
            >
              <div className="flex flex-col items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                  <Upload className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm font-medium">{t('projectHub.dropFilesHere')}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t('projectHub.orClickToSelect')}</p>
                </div>
                <label>
                  <span className="inline-flex items-center gap-2 text-sm font-medium bg-secondary px-3 py-1.5 rounded-md cursor-pointer hover:bg-secondary/80 transition-colors">
                    <FileUp className="h-4 w-4" />
                    {t('projectHub.import')}
                  </span>
                  <input
                    type="file"
                    accept=".vgsyn"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []).filter((f) => f.name.endsWith('.vgsyn'))
                      setImportFiles((prev) => [...prev, ...files])
                    }}
                    className="hidden"
                  />
                </label>
              </div>
            </div>

            {importFiles.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  {t('projectHub.filesSelected').replace('{count}', importFiles.length.toString())}
                </p>
                <div className="max-h-32 overflow-y-auto space-y-1.5">
                  {importFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between gap-2 rounded-md bg-muted/50 px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileUp className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-sm truncate">{file.name}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setImportFiles((prev) => prev.filter((_, i) => i !== index))}
                        className="shrink-0 p-1 rounded hover:bg-muted transition-colors"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setDialogType(null); setImportFiles([]) }}>{t('projectHub.cancel')}</Button>
            <Button onClick={handleImport} disabled={importFiles.length === 0}>
              {t('projectHub.import')} {importFiles.length > 0 && `(${importFiles.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteConfirm !== null} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteConfirm?.type === 'folder' ? t('projectHub.deleteFolderTitle') : t('projectHub.deleteProjectTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteConfirm?.type === 'folder'
                ? t('projectHub.deleteFolderDesc').replace('{name}', deleteConfirm?.name || '')
                : t('projectHub.deleteProjectDesc').replace('{name}', deleteConfirm?.name || '')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('projectHub.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteConfirm?.type === 'folder' ? handleDeleteFolder : handleDeleteProject}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('projectHub.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
